import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DataService } from '../http/dataService';
import { Result } from '../interfaces/helpers';
import { CrudService, EntityServiceConfig } from '../interfaces/internal';
import { mapResult, PossibleResult } from '../lib/utils';
import { HttpHeaders } from '@angular/common/http';
import { environment } from 'env/config';

export class Repository<T> implements CrudService<T> {
  url: string;
  api: string;
  public headers: HttpHeaders | { [p: string]: string | string[] } = {};
  public dataService: DataService;

  constructor({ url, client }: EntityServiceConfig) {
    this.dataService = client;
    this.url = url;
  }

  getOne<I>(
    queryParams: any,
    id?: string,
    headers?: HttpHeaders | { [p: string]: string | string[] }
  ): Observable<I | T> {
    const url = id ? `${this.url}${this.api}/${id}` : `${this.url}${this.api}`;
    const hds = headers ?? this.headers;
    return this.dataService.get<I>(url, queryParams, hds).pipe(
      take(1),
      map((result: any) => {
        if (result.items) {
          return result.items.length !== 0 ? result.items[0] : {};
        } else if (Array.isArray(result)) {
          return result[0];
        } else {
          return result;
        }
      })
    );
  }

  save(item: any): Observable<T> {
    const url = `${this.url}${this.api}`;

    return this.dataService.post<T>(url, item).pipe(take(1));
  }

  update(item: any, id: string): Observable<T> {
    const url = `${this.url}${this.api}/${id}`;

    return this.dataService.put<T>(url, item).pipe(take(1));
  }

  deleteOverAllTerritoryWalkLists(territoryID: any): Observable<T> {
    const url = `${this.url}${this.api}/${territoryID}/optimization`;

    return this.dataService.delete<T>(url);
  }

  delete(id?: string): Observable<T> {
    const url = id ? `${this.url}${this.api}/${id}` : `${this.url}${this.api}`;

    return this.dataService.delete<T>(url).pipe(take(1));
  }

  bulkAction(items: T[]): void {
    throw new Error('Method not implemented.');
  }

  setUrl(url: string): void {
    this.url = url;
  }

  setApi(api: string): void {
    this.api = api;
  }

  getAll<I>(
    queryParams?: any,
    headers?: any,
    responseType?: string
  ): Observable<Result<T>> {
    const url = `${this.url}${this.api}`;
    return this.dataService
      .get<PossibleResult<I>>(url, queryParams, headers, null, responseType)
      .pipe(
        take(1),
        map((result) => mapResult<I, T>(result, this.createFromDTO))
      );
  }

  loadDataLazily<I>(queryParams?: any): Observable<Result<T>> {
    const url = `${this.url}${this.api}/query`;
    return this.dataService.getDataByLazyLoading(url, queryParams).pipe(
      take(1),
      map((result) => mapResult<I, T>(result, this.createFromDTO))
    );
  }

  getWalkListProgress<I>(queryParams?: any): Observable<Result<T>> {
    const url = `${this.url}${this.api}/progress`;
    return this.dataService.getWalkListProgress(url, queryParams);
  }

  post<I>(data?: any, headers?: any, queryParams?: any): Observable<any> {
    const url = `${this.url}${this.api}`;
    return this.dataService.post(url, data, headers, queryParams).pipe(take(1));
  }

  getAllByPost<I>(
    data: any,
    queryParams?: any,
    headers?: any
  ): Observable<Result<T>> {
    const url = `${this.url}${this.api}`;
    return this.dataService
      .post<PossibleResult<I>>(url, data, headers, queryParams)
      .pipe(
        take(1),
        map((result) => mapResult<I, T>(result, this.createFromDTO))
      );
  }

  createFromDTO(item): T {
    return item;
  }
}
